import axios from 'axios';
import env from 'src/env.json';
import { AuthUtils } from 'src/services/Auth/Auth.utils';
import { FileAttachment } from '../AiAgent/ChatWebSocketClient';
import { Logger } from 'src/utils/logger';

// Define the base URL for API calls
const BE_API_URL = env.API_URL || 'http://localhost/api';
const PROXY_API_URL = env.API_PROXY_URL || 'http://localhost:5333';

/**
 * AiAgentApiService
 *
 * A shared service for interacting with the AI Agent API endpoints.
 * This service provides centralized methods for common operations like:
 * - Getting the agent ID
 * - Fetching media items
 *
 * It can be used by both the MediaService and RequestMediaService to prevent code duplication.
 */
export class AiAgentApiService {
  private agentId: string | null = null;
  private readonly _beApiUrl: string;
  private readonly _proxyApiUrl: string;
  private static cache = new Map<string, { data: any; timestamp: number }>();
  private static CACHE_TTL = 60000; // 1 minute cache validity

  constructor(beApiUrl: string = BE_API_URL, proxyApiUrl: string = PROXY_API_URL) {
    this._beApiUrl = beApiUrl;
    this._proxyApiUrl = proxyApiUrl;
  }

  /**
   * Get the base URL for API calls
   */
  get beApiUrl(): string {
    return this._beApiUrl;
  }

  get proxyApiUrl(): string {
    return this._proxyApiUrl.replace(/\/+$/, '');
  }

  /**
   * Helper method to get auth headers for API calls
   */
  get headers(): Record<string, string> {
    // Get the token using the proper AuthUtils method
    const jwt = AuthUtils.getJwt();

    const headers: Record<string, string> = {
      Authorization: `Bearer ${jwt.token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    // Add x-brand-id header if a brand is selected
    const selectedBrandId = this.getSelectedBrandId();
    if (selectedBrandId) {
      headers['x-brand-id'] = selectedBrandId;
    }

    return headers;
  }

  /**
   * Get the selected brand ID from cookie (same as BrandSelectionContext uses)
   */
  private getSelectedBrandId(): string | null {
    const BRAND_COOKIE_NAME = 'selectedBrandId';
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${BRAND_COOKIE_NAME}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }

  /**
   * Gets the agent ID, fetching it first if not already available
   * @returns Promise with the agent ID string
   */
  async getAgentId(): Promise<string> {
    if (this.agentId) {
      return this.agentId;
    }

    await this.initializeAgentId();
    return this.agentId || '';
  }

  /**
   * Fetches the agent ID from the backend
   * This should be called before any API requests that require the agent_id
   */
  async initializeAgentId(): Promise<void> {
    // First, clear any problematic cached agent ID
    if (localStorage.getItem('be_creatives_agent_id') === 'default-agent-id-for-testing') {
      Logger.warn('Removing test agent ID from localStorage', 'AiAgentApiService');
      localStorage.removeItem('be_creatives_agent_id');
    }

    // Check for cached agent ID in localStorage
    const cachedAgentId = localStorage.getItem('be_creatives_agent_id');
    if (cachedAgentId) {
      this.agentId = cachedAgentId;
      return;
    }

    try {
      // Fix the base URL to ensure proper API path construction
      let baseUrl = this.beApiUrl;
      // Remove any trailing slashes
      if (baseUrl.endsWith('/')) {
        baseUrl = baseUrl.slice(0, -1);
      }

      // Ensure we have /api in the path
      let endpoint;
      if (baseUrl.endsWith('/api')) {
        // If URL ends with /api, use it directly
        endpoint = `${baseUrl}/ai-agent`;
      } else {
        // Otherwise ensure we add /api
        endpoint = `${baseUrl}/api/ai-agent`;
      }

      const response = await axios.get(endpoint, {
        headers: this.headers,
      });

      // Handle various response formats
      let agentId: string | null = null;

      if (response.data && response.data.id) {
        // Standard format: { id: "agent-id" }
        agentId = response.data.id;
      } else if (response.data && response.data.agent && response.data.agent.id) {
        // Alternative format: { agent: { id: "agent-id" } }
        agentId = response.data.agent.id;
      } else if (response.data && response.data.data && response.data.data.id) {
        // Wrapper format: { data: { id: "agent-id" } }
        agentId = response.data.data.id;
      } else {
        Logger.error(
          'Unexpected response format from agent API',
          'AiAgentApiService',
          response.data,
        );
        throw new Error('Could not extract agent ID from response');
      }

      if (agentId) {
        // Save to local state and localStorage
        this.agentId = agentId;
        localStorage.setItem('be_creatives_agent_id', agentId);
      } else {
        throw new Error('No agent ID returned from server');
      }
    } catch (error: any) {
      Logger.error('Error initializing agent ID', 'AiAgentApiService', error);
      throw new Error(`Failed to initialize agent ID: ${error.message}`);
    }
  }

  /**
   * Gets an upload URL for media files
   *
   * @param payload - Request payload containing file information and path
   * @returns Promise with the upload URL and token
   */
  async getUploadUrl(payload: {
    file_name: string;
    file_size: number;
    user_path: string[];
    directory_id?: string;
    brandId?: string; // Brand ID to pass as user_id parameter
    [key: string]: any;
  }): Promise<{ upload_url: string; upload_token: string }> {
    try {
      // Ensure we have the agent ID
      const agentId = await this.getAgentId();

      // Add agent ID to payload
      const requestPayload: any = {
        ...payload,
        agent_id: agentId, // Ensure agent_id is included
      };

      // Add brand ID as user_id if provided
      if (payload.brandId) {
        requestPayload.user_id = payload.brandId;
        Logger.debug('Adding user_id to payload:', 'AiAgentApiService', payload.brandId);
        // Remove brandId from payload since it's not a backend parameter
        delete requestPayload.brandId;
      }

      // Make the API call with a timeout to prevent hanging
      const response = await axios.post(
        `${this.beApiUrl}/ai-agent/media/get-upload-url`,
        requestPayload,
        {
          headers: this.headers,
          timeout: 15000, // 15 second timeout to prevent indefinite hanging
        },
      );

      // Extract response data
      const responseData = response.data;

      if (!responseData.upload_url || !responseData.upload_token) {
        throw new Error('Invalid response: missing upload_url or upload_token');
      }

      return {
        upload_url: responseData.upload_url,
        upload_token: responseData.upload_token,
      };
    } catch (error: any) {
      Logger.error('Error getting upload URL', 'AiAgentApiService', error);
      throw new Error(`Failed to get upload URL: ${error.message}`);
    }
  }

  /**
   * Gets media items from the AI Agent API
   *
   * @param params - Query parameters for the API request
   * @param params.path - Directory path to filter by
   * @param params.mime_type - MIME type to filter by
   * @param params.search - Search query to filter by
   * @param params.query - Alternative search query parameter
   * @param params.user_id - Brand ID to filter by (backend expects brand ID as user_id)
   * @param forceRefresh - If true, bypass cache and force a new API request
   * @returns Promise with the media response
   */
  async getMediaItems(
    params: {
      path?: string;
      mime_type?: string;
      search?: string;
      query?: string;
      user_id?: string; // Brand ID for filtering media by brand
      [key: string]: any;
    },
    forceRefresh = false,
  ): Promise<any> {
    try {
      // Ensure we have the agent ID
      const agentId = await this.getAgentId();

      // Add agent ID to params
      const queryParams = {
        agent_id: agentId,
        ...params,
      };

      // Generate cache key based on the query parameters
      const cacheKey = `media:${JSON.stringify(queryParams)}`;

      // Check cache first (unless forceRefresh is true)
      const now = Date.now();
      const cachedData = AiAgentApiService.cache.get(cacheKey);

      if (!forceRefresh && cachedData && now - cachedData.timestamp < AiAgentApiService.CACHE_TTL) {
        return cachedData.data;
      }

      if (forceRefresh) {
      }

      // Make the API request
      const response = await axios.get(`${this.beApiUrl}/ai-agent/media`, {
        headers: this.headers,
        params: queryParams,
      });

      // Process the response data to ensure URL properties are set properly
      const processedData = this.processMediaResponse(response.data);

      // Cache the processed result
      AiAgentApiService.cache.set(cacheKey, {
        data: processedData,
        timestamp: now,
      });

      return processedData;
    } catch (error: any) {
      Logger.error('Error fetching media items', 'AiAgentApiService', error);
      throw error;
    }
  }

  /**
   * Process media response to ensure all files have the necessary URL properties
   * for proper display in the media gallery components
   *
   * @param data - The raw response data from the API
   * @returns The processed response data with URL properties added
   */
  /**
   * Analyzes a media item using AI to generate tags and descriptions
   *
   * @param mediaId - ID of the media to analyze
   * @param force - Whether to force reanalysis even if already analyzed
   * @returns Promise with the analysis response
   */
  async analyzeMedia(mediaId: string, force = false): Promise<any> {
    try {
      // Construct the proper API endpoint
      const endpoint = `${this.beApiUrl}/ai-agent/media/${mediaId}/analyze`;

      // Prepare payload - only include force if it's true
      const payload = force ? { force: true } : {};

      const response = await axios.post(endpoint, payload, {
        headers: this.headers,
      });

      return response.data;
    } catch (error) {
      Logger.error('Error analyzing media', 'AiAgentApiService', error);
      throw error;
    }
  }

  /**
   * Checks the analysis status of a media item
   *
   * @param mediaId - ID of the media to check
   * @returns Promise with the current analysis state
   */
  async checkAnalysisStatus(mediaId: string): Promise<any> {
    try {
      // Use the same endpoint with POST method to check status (without force flag)
      const endpoint = `${this.beApiUrl}/ai-agent/media/${mediaId}/analyze`;

      // Use an empty payload (without force flag) to check status
      const response = await axios.post(
        endpoint,
        {},
        {
          headers: this.headers,
        },
      );

      return response.data;
    } catch (error) {
      Logger.error('Error checking analysis status', 'AiAgentApiService', error);
      throw error;
    }
  }

  private processMediaResponse(data: any): any {
    // Create a deep copy to avoid modifying the original response
    const processedData = { ...data };

    // Define an interface for the media file structure
    interface MediaFile {
      id: string;
      file_name?: string;
      mime_type?: string;
      url?: string;
      index_url?: string;
      custom_properties?: {
        originalUrl?: string;
        [key: string]: any;
      };
      video?: {
        thumbnail_url?: string;
        [key: string]: any;
      };
      generated_conversions?: {
        thumbnail?: string;
        [key: string]: any;
      };
      [key: string]: any; // Allow for additional properties
    }

    // Process files array if it exists
    if (processedData.files && Array.isArray(processedData.files)) {
      processedData.files = processedData.files.map((file: MediaFile) => {
        // Create a copy of the file to avoid modifying the original
        const processedFile = { ...file };

        // Ensure URL properties are set
        if (!processedFile.url && processedFile.index_url) {
          processedFile.url = processedFile.index_url;
        }

        // Add URL to custom_properties if it doesn't exist
        if (
          processedFile.url &&
          (!processedFile.custom_properties || !processedFile.custom_properties.originalUrl)
        ) {
          processedFile.custom_properties = processedFile.custom_properties || {};
          processedFile.custom_properties.originalUrl = processedFile.url;
        }

        // Handle thumbnails for video types
        if (processedFile.mime_type && processedFile.mime_type.startsWith('video/')) {
          // Create video property if it doesn't exist
          processedFile.video = processedFile.video || {};

          // Set thumbnail URL if not already set
          if (!processedFile.video.thumbnail_url && processedFile.url) {
            processedFile.video.thumbnail_url = processedFile.url;
          }
        }

        // Ensure we have generated_conversions for thumbnail handling
        if (
          processedFile.url &&
          (!processedFile.generated_conversions || !processedFile.generated_conversions.thumbnail)
        ) {
          processedFile.generated_conversions = processedFile.generated_conversions || {};
          processedFile.generated_conversions.thumbnail = processedFile.url;
        }

        return processedFile;
      });
    }

    // Handle case where data is nested in 'data' property
    if (processedData.data && processedData.data.files && Array.isArray(processedData.data.files)) {
      processedData.data.files = processedData.data.files.map((file: MediaFile) => {
        // Process nested files with the same logic
        const processedFile = { ...file };

        if (!processedFile.url && processedFile.index_url) {
          processedFile.url = processedFile.index_url;
        }

        if (
          processedFile.url &&
          (!processedFile.custom_properties || !processedFile.custom_properties.originalUrl)
        ) {
          processedFile.custom_properties = processedFile.custom_properties || {};
          processedFile.custom_properties.originalUrl = processedFile.url;
        }

        if (processedFile.mime_type && processedFile.mime_type.startsWith('video/')) {
          processedFile.video = processedFile.video || {};
          if (!processedFile.video.thumbnail_url && processedFile.url) {
            processedFile.video.thumbnail_url = processedFile.url;
          }
        }

        if (
          processedFile.url &&
          (!processedFile.generated_conversions || !processedFile.generated_conversions.thumbnail)
        ) {
          processedFile.generated_conversions = processedFile.generated_conversions || {};
          processedFile.generated_conversions.thumbnail = processedFile.url;
        }

        return processedFile;
      });
    }

    return processedData;
  }

  public async getChatSessions(params: { 
    brand_id: string;
    flags?: string[]; // Filter by specific flags (e.g., ['favorite'])
    excludeFlags?: string[]; // Exclude sessions with these flags (e.g., ['hidden'])
  }): Promise<any[]> {
    try {
      // Construct the proper API endpoint
      const endpoint = `${this.beApiUrl}/ai-agent/chat/${params.brand_id}`;

      // Build query parameters
      const queryParams = new URLSearchParams();
      
      if (params.flags && params.flags.length > 0) {
        queryParams.append('flags', params.flags.join(','));
      }
      
      if (params.excludeFlags && params.excludeFlags.length > 0) {
        queryParams.append('excludeFlags', params.excludeFlags.join(','));
      }
      
      const url = queryParams.toString() ? `${endpoint}?${queryParams.toString()}` : endpoint;

      const response = await axios.get(url, {
        headers: this.headers,
      });

      const items = Array.isArray(response.data) ? response.data : [];
      return items as any[];
    } catch (error) {
      Logger.error('Error getting chat sessions', 'AiAgentApiService', error);
      throw error;
    }
  }

  /**
   * Get messages for a specific chat session (lazy loading)
   * Calls: GET /api/ai-agent/chat/{brand_id}/{session_id}/messages
   */
  public async getSessionMessages(params: { 
    brand_id: string; 
    session_id: string;
  }): Promise<any> {
    try {
      const endpoint = `${this.beApiUrl}/ai-agent/chat/${params.brand_id}/${params.session_id}/messages`;
      
      Logger.info('Fetching session messages', 'AiAgentApiService', {
        brand_id: params.brand_id,
        session_id: params.session_id
      });
      
      const response = await axios.get(endpoint, {
        headers: this.headers,
      });
      
      // Response format: { sessionId, messages, lastAccessed, ttl }
      return response.data;
    } catch (error) {
      Logger.error('Failed to fetch session messages', 'AiAgentApiService', error);
      throw error;
    }
  }

  public async deleteChatSession(params: {
    brand_id: string;
    chat_session_id: string;
  }): Promise<void> {
    try {
      // Construct the proper API endpoint
      const endpoint = `${this.beApiUrl}/ai-agent/chat/${params.brand_id}/${params.chat_session_id}`;

      await axios.delete(endpoint, {
        headers: this.headers,
      });
    } catch (error) {
      Logger.error('Error deleting chat session', 'AiAgentApiService', error);
      throw error;
    }
  }

  /**
   * Update chat history metadata
   * Can update: title, summary, duration_minutes, message_count, topics, flags
   * Calls: PATCH /api/ai-agent/chat/{chatHistoryId}
   */
  public async updateChatHistory(params: {
    chat_history_id: string;
    data: {
      title?: string;
      summary?: string;
      duration_minutes?: number;
      message_count?: number;
      topics?: string[];
      flags?: string[];
    };
  }): Promise<{ success: boolean }> {
    try {
      const endpoint = `${this.beApiUrl}/ai-agent/chat/${params.chat_history_id}`;

      const response = await axios.patch(endpoint, params.data, {
        headers: this.headers,
      });

      return response.data;
    } catch (error) {
      Logger.error('Error updating chat history', 'AiAgentApiService', error);
      throw error;
    }
  }

  public async uploadChatFile(payload: {
    brand_id: string;
    chat_session_id: string;
    file: File;
  }): Promise<FileAttachment> {
    try {
      const agentId = await this.getAgentId();
      // Construct the proper API endpoint
      const endpoint = `${this.proxyApiUrl}/api/chat/upload`;
      const formData = new FormData();
      formData.append('file', payload.file);
      formData.append('agentId', agentId);
      formData.append('userId', payload.brand_id);
      formData.append('sessionId', payload.chat_session_id);

      const response = await axios.post(
        endpoint,
        {
          agentId: agentId,
          userId: payload.brand_id,
          sessionId: payload.chat_session_id,
          file: payload.file,
        },
        {
          headers: {
            ...this.headers,
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      const file = response?.data?.file;
      if (!file) {
        throw new Error('Failed to upload chat file');
      }

      return {
        bucket: file.bucket,
        id: file.id,
        fileName: file.fileName,
        mimeType: file.mimeType,
        size: file.size,
        url: file.url,
      };
    } catch (error) {
      Logger.error('Error uploading chat file', 'AiAgentApiService', error);
      throw error;
    }
  }

  public async getMappedMemories(props: {
    brand_id: string;
    startDate: Date;
    endDate: Date;
  }): Promise<any[]> {
    try {
      const params = new URLSearchParams();
      params.append('userId', props.brand_id);
      params.append('startDate', props.startDate.toISOString());
      params.append('endDate', props.endDate.toISOString());
      params.append('labels', 'content-plan');
      const endpoint = `${this.beApiUrl}/ai-agent/mapped-memory?${params.toString()}`;

      const response = await axios.get(endpoint, {
        headers: this.headers,
      });

      const items = Array.isArray(response.data) ? response.data : [];
      return items as any[];
    } catch (error) {
      Logger.error('Error getting mapped memories', 'AiAgentApiService', error);
      throw error;
    }
  }

  public async getStatusBrandProfileCreation(props: { brand_id: string }): Promise<{
    status: 'need_restart' | 'success' | 'pending' | 'not_run';
  }> {
    try {
      const endpoint = `${this.beApiUrl}/brands/status-brand-profile-creation/${props.brand_id}`;
      const response = await axios.get(endpoint, {
        headers: this.headers,
      });

      return {
        status: response?.data?.status,
      };
    } catch (error) {
      Logger.error('Error getting brand profile creation status:', 'AiAgentApiService', error);
      throw error;
    }
  }

  public async updateContentPlanUsed(contentPlanId: string): Promise<void> {
    try {
      const endpoint = `${this.beApiUrl}/ai-agent/mapped-memory/${contentPlanId}`;
      await axios.put(
        endpoint,
        {
          labels: ['content-plan'],
          attributes: {
            used: true,
          },
        },
        {
          headers: this.headers,
        },
      );
    } catch (error) {
      Logger.error('Error updating content plan used status', 'AiAgentApiService', error);
      throw error;
    }
  }

  /**
   * Updates the release date of a content plan
   *
   * @param contentPlanId - ID of the content plan to update
   * @param releaseDate - New release date for the content plan
   * @returns Promise<void>
   */
  public async updateContentPlanReleaseDate(contentPlanId: string, releaseDate: Date): Promise<void> {
    try {
      const endpoint = `${this.beApiUrl}/ai-agent/mapped-memory/${contentPlanId}`;
      await axios.put(
        endpoint,
        {
          labels: ['content-plan'],
          attributes: {
            'release-date': releaseDate.toISOString(),
          },
        },
        {
          headers: this.headers,
        },
      );
      
      Logger.info(`Successfully updated content plan release date: ${contentPlanId} to ${releaseDate.toISOString()}`, 'AiAgentApiService');
    } catch (error) {
      Logger.error('Error updating content plan release date', 'AiAgentApiService', error);
      throw error;
    }
  }

  public async requestStatusBrandProfileCreation(props: { brand_id: string }): Promise<boolean> {
    try {
      const endpoint = `${this.beApiUrl}/brands/request-brand-profile-creation/${props.brand_id}`;
      await axios.post(
        endpoint,
        {},
        {
          headers: this.headers,
        },
      );

      return true;
    } catch (error) {
      Logger.error('Error getting brand profile creation status:', 'AiAgentApiService', error);
      throw error;
    }
  }

  /**
   * Deletes a mapped memory from the AI Agent
   *
   * @param contentPlanId - ID of the content plan/memory to delete
   * @returns Promise<void>
   */
  public async deleteMappedMemory(contentPlanId: string): Promise<void> {
    try {
      const endpoint = `${this.beApiUrl}/ai-agent/mapped-memory/${contentPlanId}`;
      
      await axios.delete(endpoint, {
        headers: this.headers,
      });
      
      Logger.info(`Successfully deleted content plan: ${contentPlanId}`, 'AiAgentApiService');
    } catch (error) {
      Logger.error('Error deleting mapped memory', 'AiAgentApiService', error);
      throw error;
    }
  }

  /**
   * Adds a new mapped memory to the AI Agent
   *
   * @param props - Object containing brand_id and memory data
   * @param props.brand_id - ID of the brand to associate with the memory
   * @param props.title - Title of the memory
   * @param props.content - Content of the memory
   * @param props.labels - Array of labels to categorize the memory
   * @param props.attributes - Object containing additional attributes for the memory
   * @returns Promise with the created memory data
   */
  public async addMappedMemory(props: {
    title: string;
    content: string;
    labels: string[];
    attributes: Record<string, any>;
  }): Promise<any> {
    try {
      const endpoint = `${this.beApiUrl}/ai-agent/mapped-memory`;

      const response = await axios.post(
        endpoint,
        {
          title: props.title,
          content: props.content,
          labels: props.labels,
          attributes: props.attributes,
        },
        {
          headers: this.headers,
        },
      );

      return response.data;
    } catch (error) {
      Logger.error('Error adding mapped memory', 'AiAgentApiService', error);
      throw error;
    }
  }
}

// Export a singleton instance for easy importing
export const aiAgentApiService = new AiAgentApiService();
export default aiAgentApiService;
